/**
 * Date helpers for seasons. A season covers whole days in the host's local
 * time. Game nights are stored as UTC instants, so a 7pm Tuesday game in
 * Colorado is stored as Wednesday in UTC. To include that game, the app
 * sends the season bounds as local instants:
 *   start = local midnight of the first day
 *   end   = the last millisecond of the final day
 */

function dayParts(day: string): [number, number, number] {
  const [y = NaN, m = NaN, d = NaN] = day.split('-').map(Number);
  return [y, m, d];
}

// 'YYYY-MM-DD' -> ISO instant at local midnight of that day.
export function startOfDayIso(day: string): string {
  const [y, m, d] = dayParts(day);
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
}

// 'YYYY-MM-DD' -> ISO instant at the last millisecond of that local day.
export function endOfDayIso(day: string): string {
  const [y, m, d] = dayParts(day);
  return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
}

// ISO instant -> 'YYYY-MM-DD' for the local day, for a date input.
export function toDayInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

// Short local date for tables, for example "Sep 9, 2026".
export function fmtSeasonDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Parse "10, 6, 3" into [10, 6, 3]. Returns null when any part is not a
// whole number of 0 or more.
export function parsePointsTable(text: string): number[] | null {
  const parts = text
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const nums = parts.map((p) => Number(p));
  if (nums.some((n) => !Number.isInteger(n) || n < 0)) return null;
  return nums;
}

// 1 -> "1st", 2 -> "2nd", 11 -> "11th", 23 -> "23rd".
export function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

export type LeftEarlyRule = 'KEEP_POINTS' | 'ZERO';
export type Tiebreaker = 'WINS' | 'NIGHTS_ATTENDED' | 'BEST_NIGHT' | 'LATEST_PLACEMENT' | 'NONE';

// The criteria a host can put in the tiebreaker list, in menu order.
export const TIEBREAKER_OPTIONS: { value: Exclude<Tiebreaker, 'NONE'>; label: string }[] = [
  { value: 'WINS', label: 'More nights won' },
  { value: 'NIGHTS_ATTENDED', label: 'More nights attended' },
  { value: 'BEST_NIGHT', label: 'Higher best single-night score' },
  { value: 'LATEST_PLACEMENT', label: 'Better place on the latest night' },
];

const SHORT_TIEBREAK: Record<Exclude<Tiebreaker, 'NONE'>, string> = {
  WINS: 'wins',
  NIGHTS_ATTENDED: 'nights attended',
  BEST_NIGHT: 'best night',
  LATEST_PLACEMENT: 'latest place',
};

// Drop NONE and everything after it. [] means "tied teams share a rank".
export function activeTiebreakers(list: Tiebreaker[] | null | undefined): Exclude<Tiebreaker, 'NONE'>[] {
  const out: Exclude<Tiebreaker, 'NONE'>[] = [];
  for (const t of list ?? []) {
    if (t === 'NONE') break;
    if (!out.includes(t)) out.push(t);
  }
  return out;
}

export interface SeasonRuleLike {
  scoringMode: 'TOTAL_POINTS' | 'PLACEMENT_POINTS';
  placementPoints: number[];
  bestNights: number | null;
  attendanceBonus: number;
  leftEarly?: LeftEarlyRule;
  tiebreakers?: Tiebreaker[];
}

// One-line plain description of a scoring rule for hosts and players.
export function describeRule(rule: SeasonRuleLike): string {
  const parts: string[] = [];
  if (rule.scoringMode === 'PLACEMENT_POINTS') {
    const table = rule.placementPoints.map((p, i) => `${ordinal(i + 1)} ${p}`).join(', ');
    parts.push(table ? `Placement points (${table})` : 'Placement points');
  } else {
    parts.push('Total points');
  }
  if (rule.bestNights) parts.push(`best ${rule.bestNights} nights count`);
  if (rule.attendanceBonus) {
    parts.push(`+${rule.attendanceBonus} per night attended`);
  }
  if (rule.leftEarly === 'ZERO') parts.push('left early scores 0');
  if (rule.tiebreakers) {
    const tb = activeTiebreakers(rule.tiebreakers);
    parts.push(tb.length ? `ties: ${tb.map((t) => SHORT_TIEBREAK[t]).join(', ')}` : 'ties share a rank');
  }
  return parts.join(' · ');
}
